
// src/pages/school/SchoolProfile.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, Form, Button, Spinner, InputGroup
} from "react-bootstrap";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  doc, onSnapshot, setDoc, serverTimestamp,
  collection, getCountFromServer, deleteField
} from "firebase/firestore";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";
import { useAuth } from "../../context/AuthContext";
import { storage } from "../../services/firebase";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from "firebase/storage";

/* ------------- Helpers ------------- */
const schoolRef = (schoolId) => doc(db, "schools", schoolId);
const studentsCol = (schoolId) => collection(db, "schools", schoolId, "students");
const teachersCol = (schoolId) => collection(db, "schools", schoolId, "teachers");

const feeKey = (schoolId, uid) => `feeAuth:${schoolId}:${uid}`;
function hasFeeAuthed(schoolId, uid) {
  try {
    const raw =
      sessionStorage.getItem(feeKey(schoolId, uid)) ||
      localStorage.getItem(feeKey(schoolId, uid));
    if (!raw) return false;
    return !!JSON.parse(raw)?.ok;
  } catch {
    return false;
  }
}
function fmtIN(n) {
  try { return new Intl.NumberFormat("en-IN").format(n ?? 0); }
  catch { return String(n ?? 0); }
}

/* ---------- Storage logo resolver (gs://photos-oysi5) ---------- */
const BRAND_BUCKET_URL = "gs://photos-oysi5";
async function tryGetURL(gsPath) {
  try { return await getDownloadURL(ref(storage, gsPath)); }
  catch { return null; }
}
async function resolveSchoolLogoURL(schoolId) {
  if (!schoolId) return null;
  const base = `${BRAND_BUCKET_URL}/schools/${schoolId}/branding`;
  // quick common names
  const candidates = [
    "logo.png","logo.jpg","logo.jpeg","logo.webp","logo.svg",
    "brand.png","brand.jpg","brand.jpeg","brand.webp","brand.svg",
    "emblem.png","emblem.jpg","emblem.jpeg","emblem.webp","emblem.svg",
  ];
  for (const name of candidates) {
    const u = await tryGetURL(`${base}/${name}`);
    if (u) return u;
  }
  // fallback: list all + pick best image
  try {
    const res = await listAll(ref(storage, base));
    const items = res.items || [];
    if (!items.length) return null;
    const imageExt = /\.(png|jpg|jpeg|webp|svg)$/i;
    const ok = items.filter((x) => imageExt.test(x.name));
    const prefer = (x) => /logo|brand|emblem/i.test(x.name);
    const best = ok.find(prefer) || ok[0] || items[0];
    if (!best) return null;
    return await getDownloadURL(best);
  } catch {
    return null;
  }
}

/* ------------- Component ------------- */
export default function SchoolProfile() {
  const navigate = useNavigate();
  const { user, isSchoolAdmin, loading: authLoading } = useAuth();
  const { school, loading: loadingSchool } = useSchool();


  // IDs
  const uid = user?.uid || null;
  const schoolId = school?.id || null;
  const feeUnlocked = useMemo(
    () => !!(uid && schoolId && hasFeeAuthed(schoolId, uid)),
    [uid, schoolId]
  );
  // UI states (all hooks at top)
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [countsLoading, setCountsLoading] = useState(true);
  const [studentCount, setStudentCount] = useState(0);
  const [teacherCount, setTeacherCount] = useState(0);

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const [removingLogo, setRemovingLogo] = useState(false);
  const fileInputRef = React.useRef(null);
  const pickLogoFile = () => fileInputRef.current?.click();

  // Live school data
  const [form, setForm] = useState({
    name: "",
    code: "",
    principal: "",
    phone: "",
    email: "",
    website: "",
    academicYear: "",
    logoUrl: "",
    logoPath: "", // store full gs:// path for safe cross-bucket deletes
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    theme: { primary: "#0d6efd", sidebarBg: "#ffffff" },
    updatedAt: null,
    updatedBy: null,
  });

  // Preview/logo resolver (uses form.logoUrl if present, else gs:// lookup)
  const [logoPreview, setLogoPreview] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!schoolId) return;
      const cacheKey = `ls:logo:${schoolId}`;

      // if Firestore already has a URL, prefer it & cache it
      if (form.logoUrl) {
        if (alive) setLogoPreview(form.logoUrl);
        try { localStorage.setItem(cacheKey, form.logoUrl); } catch {}
        return;
      }

      // use cached resolved URL
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) { if (alive) setLogoPreview(cached); }
      } catch {}

      // resolve from Storage
      const url = await resolveSchoolLogoURL(schoolId);
      if (!alive) return;
      if (url) {
        setLogoPreview(url);
        try { localStorage.setItem(cacheKey, url); } catch {}
      } else {
        setLogoPreview("");
      }
    })();
    return () => { alive = false; };
  }, [schoolId, form.logoUrl]);

  // Load school doc (live)
  useEffect(() => {
    if (!schoolId) { setLoadingDoc(false); return; }
    const unsub = onSnapshot(
      schoolRef(schoolId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setForm((prev) => ({
            ...prev,
            name: data.name || "",
            code: data.code || "",
            principal: data.principal || data?.contact?.principal || "",
            phone: data.phone || "",
            email: data.email || "",
            website: data.website || "",
            academicYear: data.academicYear || "",
            logoUrl: data.logoUrl || "",
            logoPath: data.logoPath || "",
            addressLine1: data.addressLine1 || "",
            addressLine2: data.addressLine2 || "",
            city: data.city || "",
            state: data.state || "",
            pincode: data.pincode || "",
            country: data.country || "India",
            theme: {
              primary: data?.theme?.primary || "#0d6efd",
              sidebarBg: data?.theme?.sidebarBg || "#ffffff",
            },
            updatedAt: data.updatedAt || null,
            updatedBy: data.updatedBy || null,
          }));
        }
        setLoadingDoc(false);
      },
      (err) => {
        setLoadingDoc(false);
        Swal.fire({
          icon: "error",
          title: "Could not load school profile",
          text: err?.message || "Check your permissions/rules.",
        });
      }
    );
    return () => unsub();
  }, [schoolId]);

  // Load quick counts
  useEffect(() => {
    (async () => {
      if (!schoolId) { setCountsLoading(false); return; }
      try {
        const [sc, tc] = await Promise.all([
          getCountFromServer(studentsCol(schoolId)),
          getCountFromServer(teachersCol(schoolId)),
        ]);
        setStudentCount(sc.data().count || 0);
        setTeacherCount(tc.data().count || 0);
      } catch {} finally {
        setCountsLoading(false);
      }
    })();
  }, [schoolId]);

  // Save handler
  const onSave = async () => {
    if (!schoolId) return;
    if (!isSchoolAdmin) {
      return Swal.fire({ icon: "info", title: "Restricted", text: "Only School Admins can update school profile." });
    }
    if (!form.name.trim()) {
      return Swal.fire({ icon: "warning", title: "School name is required" });
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        principal: form.principal.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        website: form.website.trim(),
        academicYear: form.academicYear.trim(),
        logoUrl: (form.logoUrl || "").trim(),
        logoPath: (form.logoPath || "").trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        country: form.country.trim() || "India",
        contact: {
          principal: form.principal.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          website: form.website.trim(),
        },
        theme: { primary: form?.theme?.primary || "#0d6efd", sidebarBg: form?.theme?.sidebarBg || "#ffffff" },
        updatedAt: serverTimestamp(),
        updatedBy: uid ? { uid, at: serverTimestamp() } : null,
      };
      await setDoc(schoolRef(schoolId), payload, { merge: true });
      setEditing(false);
      Swal.fire({ icon: "success", title: "School profile updated" });
    } catch (e) {
      const msg = e?.code === "permission-denied" ? "You don’t have permission to update this school." : (e?.message || "Check your permissions/rules.");
      Swal.fire({ icon: "error", title: "Could not save", text: msg });
    } finally { setSaving(false); }
  };

  // Actions
  const goFees = () => { if (feeUnlocked) navigate("/school/fees"); else navigate("/school/fees/login?next=/school/fees"); };
  const goFeesPin = () => navigate("/school/fees/login?next=/school/fees");
  const goSupport = () => navigate("/school/support");
  const goStudents = () => navigate("/school/students");
  const goTeachers = () => navigate("/school/teachers");

  // Upload (now to gs://photos-oysi5)
  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset input for next time
    if (!f || !schoolId) return;

    if (!/image\/(png|jpe?g|webp)/i.test(f.type)) {
      return Swal.fire({ icon: "info", title: "Please select a PNG, JPG or WEBP file." });
    }
    if (f.size > 3 * 1024 * 1024) {
      return Swal.fire({ icon: "info", title: "Logo too large (max 3 MB)." });
    }

    const ext = (f.name.split(".").pop() || "png").toLowerCase();
    const gsPath = `${BRAND_BUCKET_URL}/schools/${schoolId}/branding/logo_${Date.now()}.${ext}`;
    const storageRefObj = ref(storage, gsPath);

    setUploadingLogo(true);
    setLogoProgress(0);

    try {
      const task = uploadBytesResumable(storageRefObj, f, {
        contentType: f.type,
        cacheControl: "public,max-age=604800,immutable",
      });
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setLogoProgress(pct);
          },
          reject,
          resolve
        );
      });

      const url = await getDownloadURL(storageRefObj);

      // delete previous logo if path known
      const oldPath = (form.logoPath || "").trim();
      if (oldPath && oldPath !== gsPath) {
        try { await deleteObject(ref(storage, oldPath)); } catch {}
      }

      // save to Firestore
      await setDoc(
        schoolRef(schoolId),
        {
          logoUrl: url,
          logoPath: gsPath, // store full gs:// path for later deletes
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid ? { uid: user.uid, at: serverTimestamp() } : null,
        },
        { merge: true }
      );

      // update local state + preview + cache
      setForm((f) => ({ ...f, logoUrl: url, logoPath: gsPath }));
      setLogoPreview(url);
      try { localStorage.setItem(`ls:logo:${schoolId}`, url); } catch {}
      Swal.fire({ icon: "success", title: "Logo updated" });
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Upload failed", text: err?.message || "Try again." });
    } finally {
      setUploadingLogo(false);
      setLogoProgress(0);
    }
  };

  // Remove logo (also clears cache)
  const onRemoveLogo = async () => {
    if (!schoolId || !isSchoolAdmin) return;
    const res = await Swal.fire({
      icon: "warning",
      title: "Remove logo?",
      text: "This will delete the current logo file (if accessible) and clear it from the profile.",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!res.isConfirmed) return;

    setRemovingLogo(true);
    try {
      const p = (form.logoPath || "").trim();
      if (p) {
        try { await deleteObject(ref(storage, p)); } catch {}
      }

      await setDoc(
        schoolRef(schoolId),
        { logoUrl: deleteField(), logoPath: deleteField(), updatedAt: serverTimestamp(), updatedBy: uid ? { uid, at: serverTimestamp() } : null },
        { merge: true }
      );

      setForm((f) => ({ ...f, logoUrl: "", logoPath: "" }));
      setLogoPreview("");
      try { localStorage.removeItem(`ls:logo:${schoolId}`); } catch {}
      Swal.fire({ icon: "success", title: "Logo removed" });
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Could not remove logo", text: err?.message || "Try again." });
    } finally {
      setRemovingLogo(false);
    }
  };

  // Render branches via variable (no early returns)
  let content;
  if (authLoading || loadingSchool || loadingDoc) {
    content = (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading school profile…
        </Card.Body>
      </Card>
    );
  } else {
    content = (
      <>
        {/* HEADER + ACTIONS */}
        <Row className="align-items-center mb-3">
          <Col>
            <h5 className="mb-1">School Profile</h5>
            <div className="text-muted small">School ID: <code>{schoolId || "—"}</code></div>
          </Col>
          <Col xs="auto" className="d-flex flex-wrap gap-2">
            <Button variant="outline-primary" onClick={goFees}>
              {feeUnlocked ? "Open Fees Dashboard" : "Unlock Fees"}
            </Button>
            <Button variant="outline-secondary" onClick={goFeesPin}>Fee PIN</Button>
            <Button variant="outline-success" onClick={goStudents}>Students</Button>
            <Button variant="outline-success" onClick={goTeachers}>Teachers</Button>
            <Button variant="outline-dark" onClick={goSupport}>Support</Button>
          </Col>
        </Row>

        {/* STATS */}
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Students</div>
                <div className="h4 mb-0">{countsLoading ? <Spinner size="sm" animation="border" /> : fmtIN(studentCount)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Teachers</div>
                <div className="h4 mb-0">{countsLoading ? <Spinner size="sm" animation="border" /> : fmtIN(teacherCount)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Academic Year</div>
                <div className="h4 mb-0">{form.academicYear || "—"}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* PROFILE FORM */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Row className="g-3">
              {/* Logo preview */}
              <Col md={3} className="text-center">
                <div className="mb-2 position-relative">
                  <img
                    src={logoPreview || "https://via.placeholder.com/160x160?text=Logo"}
                    alt="logo"
                    style={{ width: 160, height: 160, objectFit: "contain", borderRadius: 8, border: "1px solid #eee" }}
                  />
                  {(uploadingLogo || removingLogo) && (
                    <div style={{
                      position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                      borderRadius: 8
                    }}>
                      <span>{uploadingLogo ? `${logoProgress}%` : "Removing…"}</span>
                    </div>
                  )}
                </div>

                <div className="d-grid gap-2 mb-2">
                  <Button
                    variant="outline-primary"
                    onClick={pickLogoFile}
                    disabled={!isSchoolAdmin || uploadingLogo || removingLogo}
                  >
                    {uploadingLogo ? "Uploading…" : "Upload Logo"}
                  </Button>

                  <Button
                    variant="outline-danger"
                    onClick={onRemoveLogo}
                    disabled={!isSchoolAdmin || !(form.logoUrl || logoPreview) || uploadingLogo || removingLogo}
                  >
                    {removingLogo ? "Removing…" : "Remove Logo"}
                  </Button>

                  {/* Optional: manual URL edit still allowed (when editing) */}
                  <Form.Control
                    value={form.logoUrl}
                    onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    disabled={!isSchoolAdmin || uploadingLogo || removingLogo || !editing}
                  />
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onLogoFile}
                />
              </Col>

              {/* Main details */}
              <Col md={9}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>School Name</Form.Label>
                      <Form.Control
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="e.g., Delhi Public School"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Code</Form.Label>
                      <Form.Control
                        value={form.code}
                        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                        placeholder="e.g., DPS-001"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Principal</Form.Label>
                      <Form.Control
                        value={form.principal}
                        onChange={(e) => setForm((f) => ({ ...f, principal: e.target.value }))}
                        placeholder="Principal's full name"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Website</Form.Label>
                      <Form.Control
                        value={form.website}
                        onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                        placeholder="https://example.com"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="+91 98xxxxxxxx"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="school@example.com"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Academic Year</Form.Label>
                      <Form.Control
                        value={form.academicYear}
                        onChange={(e) => setForm((f) => ({ ...f, academicYear: e.target.value }))}
                        placeholder="e.g., 2025-26"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        value={form.addressLine1}
                        onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
                        placeholder="Street / Area"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Address Line 2</Form.Label>
                      <Form.Control
                        value={form.addressLine2}
                        onChange={(e) => setForm((f) => ({ ...f, addressLine2: e.target.value }))}
                        placeholder="Landmark (optional)"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        value={form.city}
                        onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                        placeholder="City"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        value={form.state}
                        onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                        placeholder="State"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        value={form.pincode}
                        onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
                        placeholder="PIN"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        value={form.country}
                        onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                        placeholder="Country"
                        disabled={!isSchoolAdmin || !editing}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Theme – Primary</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="color"
                          value={form?.theme?.primary || "#0d6efd"}
                          onChange={(e) => setForm((f) => ({ ...f, theme: { ...f.theme, primary: e.target.value } }))}
                          disabled={!isSchoolAdmin || !editing}
                          style={{ width: 56 }}
                        />
                        <Form.Control
                          value={form?.theme?.primary || "#0d6efd"}
                          onChange={(e) => setForm((f) => ({ ...f, theme: { ...f.theme, primary: e.target.value } }))}
                          disabled={!isSchoolAdmin || !editing}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Theme – Sidebar BG</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="color"
                          value={form?.theme?.sidebarBg || "#ffffff"}
                          onChange={(e) => setForm((f) => ({ ...f, theme: { ...f.theme, sidebarBg: e.target.value } }))}
                          disabled={!isSchoolAdmin || !editing}
                          style={{ width: 56 }}
                        />
                        <Form.Control
                          value={form?.theme?.sidebarBg || "#ffffff"}
                          onChange={(e) => setForm((f) => ({ ...f, theme: { ...f.theme, sidebarBg: e.target.value } }))}
                          disabled={!isSchoolAdmin || !editing}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2 mt-3">
                  {isSchoolAdmin && !editing && (<Button onClick={() => setEditing(true)}>Edit</Button>)}
                  {isSchoolAdmin && editing && (
                    <>
                      <Button onClick={onSave} disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : "Save"}</Button>
                      <Button variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="mt-3">
          <Button variant="link" className="text-decoration-none" onClick={() => navigate("/school")}>
            ← Back to School Dashboard
          </Button>
        </div>
      </>
    );
  }

  return <Container className="py-4">{content}</Container>;
}
